import type { ChunkPayload, GeocodeResult } from '@roadworld/shared';

// In dev, VITE_API_BASE_URL is undefined → '' → relative paths → Vite proxy handles it.
// In production (Cloudflare Pages), set VITE_API_BASE_URL=https://roadworld.workers.dev
// in the Pages environment variables so the client hits the Worker instead of Pages.
// Strip any trailing slash so we never produce double-slash URLs like `//api/chunk`.
const API_ORIGIN = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '').replace(/\/$/, '');
const BASE = `${API_ORIGIN}/api`;

/** WebSocket URL for multiplayer connection. Uses the same origin (vite proxies to backend in dev). */
export function multiplayerWsUrl(): string {
  if (API_ORIGIN) {
    // Production: derive ws(s) URL from the configured API origin.
    return API_ORIGIN.replace(/^http/, 'ws') + '/socket';
  }
  // Dev: same-origin, Vite proxy forwards to the local server.
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/socket`;
}

export async function geocode(name: string): Promise<GeocodeResult | null> {
  const res = await fetch(`${BASE}/location?name=${encodeURIComponent(name)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`geocode failed: ${res.status}`);
  return (await res.json()) as GeocodeResult;
}

export interface FetchChunkOptions {
  lat: number;
  lng: number;
  /** Size of the chunk side in meters. */
  size?: number;
  /** DEM grid resolution (per side). */
  demRes?: number;
  signal?: AbortSignal;
}

export async function fetchChunk(opts: FetchChunkOptions): Promise<ChunkPayload> {
  const params = new URLSearchParams({
    lat: String(opts.lat),
    lng: String(opts.lng),
    size: String(opts.size ?? 1500),
    demRes: String(opts.demRes ?? 48),
  });
  const res = await fetch(`${BASE}/chunk?${params.toString()}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`chunk fetch failed: ${res.status}`);
  return (await res.json()) as ChunkPayload;
}
